"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { analytics } from "@/lib/analytics";
import {
  buildGoogleCalendarUrl,
  buildReservationCalendarArtifacts,
  buildReservationIcs,
  type ReservationCalendarVenue,
} from "@/lib/reservation-calendar-links";
import { cn } from "@/lib/utils";
import { formatTime12h } from "@convex/lib/reservationTime";
import { CalendarPlus, CheckCircle2, Phone, RotateCcw, Copy } from "lucide-react";
import { toast } from "sonner";

interface ReservationConfirmationProps {
  confirmationCode: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM (24h)
  partySize: number;
  customerName: string;
  venue: ReservationCalendarVenue;
  onReset: () => void;
  className?: string;
}

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}


export function ReservationConfirmation({
  confirmationCode,
  date,
  time,
  partySize,
  customerName,
  venue,
  onReset,
  className,
}: ReservationConfirmationProps) {
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(confirmationCode);
      toast.success("Confirmation code copied!");
    } catch {
      // Fallback for older browsers
      toast.info(`Your code: ${confirmationCode}`);
    }
  };

  // The guest picked a local Las Vegas wall clock; resolve it to a real UTC
  // instant so the calendar entry lands at the right hour for any device.
  const { event: calendarEvent, icsFileName, phoneDisplay, phoneHref } =
    buildReservationCalendarArtifacts({
      venue,
      confirmationCode,
      partySize,
      customerName,
      date,
      time,
    });
  const googleCalendarUrl = buildGoogleCalendarUrl(calendarEvent);

  const handleAddToCalendar = () => {
    analytics.ctaClicked("Add to calendar", "reservation_confirmation", {
      link_type: "calendar_ics",
    });

    const ics = buildReservationIcs(calendarEvent);
    const blobUrl = URL.createObjectURL(
      new Blob([ics], { type: "text/calendar;charset=utf-8" })
    );
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = icsFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const handleGoogleCalendar = () => {
    analytics.ctaClicked("Google Calendar", "reservation_confirmation", {
      link_type: "calendar_google",
      destination_url: googleCalendarUrl,
      is_external: true,
    });
  };

  return (
    <div className={cn("space-y-6", className)}>
      <Card className="animate-in fade-in zoom-in-95 border-[var(--roma-olive)]/35 bg-[var(--roma-linen)]/40 duration-300 ease-out motion-reduce:animate-none">
        <CardContent className="pt-6 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 animate-in fade-in zoom-in-50 text-[var(--roma-olive)] duration-300 ease-out motion-reduce:animate-none" />

          <h2 className="mt-4 font-serif text-2xl font-normal tracking-tight text-[var(--roma-espresso)]">
            Reservation Confirmed!
          </h2>
          <p className="mt-1 text-sm text-[var(--roma-espresso)]/72">
            We look forward to seeing you, {customerName.split(" ")[0]}.
          </p>

          {/* Confirmation Code */}
          <div className="mt-6 rounded-lg border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-[var(--roma-espresso)]/70">
              Confirmation Code
            </p>
            <button
              type="button"
              onClick={handleCopyCode}
              className="mt-1 inline-flex items-center gap-2 text-2xl font-mono font-bold tracking-widest text-[var(--roma-espresso)] hover:text-[var(--roma-terracotta)] transition-colors"
            >
              {confirmationCode}
              <Copy className="h-4 w-4 text-[var(--roma-espresso)]/55" />
            </button>
          </div>

          {/* Reservation Details */}
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex items-center justify-between border-b border-[var(--roma-parchment-line)] pb-2">
              <span className="text-[var(--roma-espresso)]/72">Date</span>
              <span className="font-medium text-[var(--roma-espresso)]">{formatDate(date)}</span>
            </div>
            <div className="flex items-center justify-between border-b border-[var(--roma-parchment-line)] pb-2">
              <span className="text-[var(--roma-espresso)]/72">Time</span>
              <span className="font-medium text-[var(--roma-espresso)]">{formatTime12h(time)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[var(--roma-espresso)]/72">Party Size</span>
              <span className="font-medium text-[var(--roma-espresso)]">
                {partySize} {partySize === 1 ? "guest" : "guests"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="space-y-3">
        <div className="space-y-2">
          <Button
            type="button"
            className="roma-cta min-h-11 w-full rounded-full"
            onClick={handleAddToCalendar}
          >
            <CalendarPlus className="mr-2 h-4 w-4" />
            Add to calendar
          </Button>
          <p className="text-center text-xs text-[var(--roma-espresso)]/72">
            Or add it to{" "}
            <a
              href={googleCalendarUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleGoogleCalendar}
              className="roma-text-link font-medium text-[var(--roma-terracotta)] underline-offset-2 hover:underline"
            >
              Google Calendar
            </a>
          </p>
        </div>

        <div className="rounded-lg border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/30 p-3 text-center">
          <p className="text-xs text-[var(--roma-espresso)]/72">
            <Phone className="mr-1 inline-block h-3 w-3" />
            Need to change or cancel? Call{" "}
            <a
              href={`tel:${phoneHref}`}
              className="font-medium text-[var(--roma-espresso)] hover:text-[var(--roma-terracotta)] transition-colors"
            >
              {phoneDisplay}
            </a>
          </p>
        </div>

        <Button
          variant="ghost"
          className="w-full text-[var(--roma-espresso)]/72"
          onClick={onReset}
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Make Another Reservation
        </Button>
      </div>
    </div>
  );
}
